import {serializeProgramComposition} from './program-composition';
import type { FrameEvidenceAttempt, MediaSegment } from './types';
import { videoSamples } from './sample-evidence';
import { mediaWitnessFormat as format } from './media-witness-format';
import type { AcceptedFrame } from './frame-evidence';

/** Ephemeral owner receipt for one exact accepted output frame. No credentials. */
export interface CausalFrameReceipt {
  frame: AcceptedFrame;
  current(): boolean;
  receipt: Readonly<{
    profile: 'monotonic-causal-v1'; purpose: 'runtime'|'program-return'|'current-video';
    challengeId: string; receiptId: string; eventId: string;
    bindingSha256: string; eventSha256: string; compositionSha256: string;
    collectorProcessId: string; collectorGeneration: string;
    originalExpiresAtMs: number; receiptSequence: number;
  }>;
}

interface ClockMapping {
  challengeId: string;
  sourceMonotonicMs: number;
  serverTimeMs: number;
  uncertaintyMs: number;
  expiresAtMs: number;
}

export interface MediaWitnessInput {
  enabled: boolean;
  segment: MediaSegment;
  attempt: FrameEvidenceAttempt;
  videoTrackId: number;
  /** Task11c clock reader revalidates the actual source and acknowledged mapping. */
  clock?: { sourceId: string; read(): ClockMapping };
  /** Runtime v2 has no ENDING server-clock/finalTick authority. */
  runtimeComposition?: boolean;
  selected?: NonNullable<MediaSegment['frameEvidence']>[number];
  causal?: CausalFrameReceipt;
}

const fail = (): never => {
  throw Error('media_witness_unavailable');
};
const nonnegative = (n: number) => Number.isFinite(n) && n >= 0 && !Object.is(n, -0);

/** Explicit local codec only. Call after splitting and before encryption; not wired to publisher. */
export async function appendMediaWitness(input: MediaWitnessInput): Promise<Uint8Array> {
  const { segment, attempt, clock } = input;
  if (!input.enabled) {
    return segment.bytes;
  }
  if (input.causal) return appendCausalWitness(input);
  const current = () => {
    if (!attempt.isCurrent() || (clock && clock.sourceId !== attempt.sourceId) || (!clock && !input.runtimeComposition) || (input.selected?.frame.compositionCurrent && !input.selected.frame.compositionCurrent())) {
      fail();
    }
    if(input.runtimeComposition)return undefined;
    const m = clock!.read();
    if (m.challengeId !== attempt.id) {
      fail();
    }
    return m;
  };
  const initialMapping = current();
  const mapping = initialMapping ? {...initialMapping} : undefined;
  if (mapping && (
    ![
      mapping.sourceMonotonicMs,
      mapping.serverTimeMs,
      mapping.uncertaintyMs,
      mapping.expiresAtMs,
    ].every(nonnegative) ||
    mapping.uncertaintyMs > 100
  )) {
    fail();
  }

  // Snapshot bytes before the asynchronous hash; queued callers cannot change the proof's sample.
  if (segment.bytes.length + format.boxBytes > format.maxFragmentBytes) {
    fail();
  }
  const plain = segment.bytes.slice();
  const v = new DataView(plain.buffer);
  let p = 0;
  for (const type of ['moof', 'mdat']) {
    if (p + 8 > plain.length || String.fromCharCode(...plain.subarray(p + 4, p + 8)) !== type) {
      fail();
    }
    const size = v.getUint32(p);
    if (size < 8 || p + size > plain.length) {
      fail();
    }
    p += size;
  }
  if (p !== plain.length) {
    fail();
  }

  const evidence = segment.frameEvidence;
  if (!evidence?.length || evidence.length > 600) {
    return fail();
  }
  const indices = new Set<number>();
  for (const e of evidence) {
    if (
      e.attempt !== attempt ||
      e.frame.sourceId !== attempt.sourceId ||
      !Number.isSafeInteger(e.sampleIndex) ||
      e.sampleIndex < 0 ||
      indices.has(e.sampleIndex)
    ) {
      fail();
    }
    indices.add(e.sampleIndex);
  }
  const e = input.selected ?? [...evidence].sort((a, b) => a.sampleIndex - b.sampleIndex)[0]!;
  if(!evidence.includes(e))fail();
  const composition=e.frame.composition;
  if(input.runtimeComposition && composition?.binding.kind!=='gaming')fail();
  const serialized=composition?serializeProgramComposition(composition):undefined;
  if(composition && (composition.binding.programFrameSourceId!==attempt.sourceId || !e.frame.compositionCurrent?.()))fail();
  const boxBytes=input.runtimeComposition?format.runtimeComposition.boxBytes:composition?format.composition.boxBytes:format.boxBytes;
  if(plain.length+boxBytes>format.maxFragmentBytes)fail();
  const originalDigest = e.encodedSampleSha256;
  if (!originalDigest) {
    fail();
  }
  const sampleOrdinal = e.sampleIndex;
  const decodeTime = e.decodeTime;
  const timescale = e.timescale;
  const captureMonotonicMs = e.frame.capturedMonotonicMs;
  const sample = videoSamples(plain, input.videoTrackId)[sampleOrdinal];
  if (
    !sample ||
    sample.decodeTime !== decodeTime ||
    !Number.isSafeInteger(decodeTime) ||
    decodeTime < 0 ||
    !Number.isSafeInteger(timescale) ||
    timescale <= 0 ||
    timescale > 0xffffffff ||
    !nonnegative(captureMonotonicMs) ||
    (mapping && captureMonotonicMs < mapping.sourceMonotonicMs)
  ) {
    fail();
  }
  const captureServerMs = mapping ? mapping.serverTimeMs + (captureMonotonicMs - mapping.sourceMonotonicMs) : 0;
  if (!nonnegative(captureServerMs) || (mapping && captureServerMs + mapping.uncertaintyMs >= mapping.expiresAtMs)) {
    fail();
  }

  const out = new Uint8Array(plain.length + boxBytes);
  const tail = out.subarray(plain.length);
  const w = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);
  const o = format.offsets;
  const uuid = (id: string, at: number) => {
    if (!new RegExp(format.uuidPattern).test(id)) {
      fail();
    }
    tail.set(id.replaceAll('-', '').match(/../g)!.map(x => parseInt(x, 16)), at);
  };
  w.setUint32(0, boxBytes);
  tail.set([117, 117, 105, 100], 4);
  tail.set(format.uuidHex.match(/../g)!.map(x => parseInt(x, 16)), 8);
  tail[o.version] = input.runtimeComposition?format.runtimeComposition.version:composition?format.composition.version:format.version;
  if(composition)tail[format.composition.purposeOffset]=format.composition.purposes[composition.binding.kind];
  uuid(attempt.id, o.challengeId);
  uuid(attempt.sourceId, o.sourceId);
  w.setUint32(o.sampleOrdinal, sampleOrdinal);
  w.setBigUint64(o.decodeTime, BigInt(decodeTime));
  w.setUint32(o.timescale, timescale);
  w.setFloat64(o.captureMonotonicMs, captureMonotonicMs);
  w.setFloat64(o.captureServerMs, captureServerMs);
  if(input.runtimeComposition){
    const upper=e.frame.captureUpperMonotonicMs;
    if(typeof upper!=='number'||!nonnegative(upper)||upper<captureMonotonicMs)fail();
    w.setFloat64(format.runtimeComposition.captureUpperMonotonicMsOffset,upper!);
  }

  const hash = await crypto.subtle.digest('SHA-256', sample.bytes.slice().buffer);
  current();
  const originalHash = await originalDigest;
  const latest = current();
  if (
    !originalHash ||
    Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('') !== originalHash
  ) {
    fail();
  }
  if (mapping && latest && (
    latest.sourceMonotonicMs !== mapping.sourceMonotonicMs ||
    latest.serverTimeMs !== mapping.serverTimeMs ||
    latest.uncertaintyMs !== mapping.uncertaintyMs ||
    latest.expiresAtMs !== mapping.expiresAtMs
  )) {
    fail();
  }
  if(serialized){const digest=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(serialized));current();if(!e.frame.compositionCurrent?.())fail();tail.set(new Uint8Array(digest),format.composition.digestOffset);}
  tail.set(new Uint8Array(hash), o.sampleSha256);
  out.set(plain);
  return out;
}

async function appendCausalWitness(input: MediaWitnessInput): Promise<Uint8Array> {
  const {segment,attempt,selected:e,causal} = input;
  if (!causal || !e || causal.frame !== e.frame) return fail();
  // Copy public scalar identity before any await; proof currentness remains owned by
  // the original document/source/receipt lifecycle, never a new codec timeout.
  const receipt = {...causal.receipt};
  const current = () => {
    if (!attempt.isCurrent() || !causal.current() || !e.frame.compositionCurrent?.() ||
        causal.frame !== e.frame || e.attempt !== attempt || e.frame.sourceId !== attempt.sourceId ||
        receipt.challengeId !== attempt.id ||
        Object.keys(receipt).some(key => receipt[key as keyof typeof receipt] !== causal.receipt[key as keyof typeof receipt])) fail();
  };
  current();
  const f = format.causalComposition;
  const composition = e.frame.composition;
  if (!composition || receipt.profile !== f.profile || !(receipt.purpose in f.purposes) ||
      (receipt.purpose === 'runtime') !== (composition.binding.kind === 'gaming') ||
      composition.binding.programFrameSourceId !== attempt.sourceId || !segment.frameEvidence?.includes(e) ||
      segment.frameEvidence.length > 600 || !e.encodedSampleSha256 ||
      !Number.isSafeInteger(e.sampleIndex) || e.sampleIndex < 0 || e.sampleIndex > f.maxSampleOrdinal ||
      !Number.isSafeInteger(e.decodeTime) || e.decodeTime < 0 ||
      !Number.isSafeInteger(e.timescale) || e.timescale < 1 || e.timescale > 0xffffffff ||
      !Number.isSafeInteger(receipt.originalExpiresAtMs) || receipt.originalExpiresAtMs < 0 ||
      !Number.isSafeInteger(receipt.receiptSequence) || receipt.receiptSequence < 1 ||
      segment.bytes.length + f.boxBytes > format.maxFragmentBytes) return fail();
  const seen = new Set<number>();
  for (const entry of segment.frameEvidence) {
    if(entry.attempt !== attempt || entry.frame.sourceId !== attempt.sourceId || seen.has(entry.sampleIndex)) fail();
    seen.add(entry.sampleIndex);
  }
  const serialized = serializeProgramComposition(composition);
  const plain = segment.bytes.slice(), view = new DataView(plain.buffer);
  let end = 0;
  for (const type of ['moof','mdat']) {
    if(end + 8 > plain.length || String.fromCharCode(...plain.subarray(end+4,end+8)) !== type) fail();
    const size = view.getUint32(end); if(size < 8 || end + size > plain.length) fail(); end += size;
  }
  if(end !== plain.length) fail();
  const sample = videoSamples(plain,input.videoTrackId)[e.sampleIndex];
  if(!sample || sample.decodeTime !== e.decodeTime) return fail();
  const tail = new Uint8Array(f.boxBytes), w = new DataView(tail.buffer);
  const offset = (name:string) => f.fields.find(field => field.name === name)!.offset;
  const hex = (value:string,name:string) => {
    if(!/^[0-9a-f]{64}$/.test(value)) fail();
    tail.set(value.match(/../g)!.map(x=>parseInt(x,16)),offset(name));
  };
  for(const name of ['challengeId','receiptId','eventId'] as const) {
    if(!new RegExp(format.uuidPattern).test(receipt[name])) fail();
    tail.set(receipt[name].replaceAll('-','').match(/../g)!.map(x=>parseInt(x,16)),offset(name));
  }
  for(const name of ['bindingSha256','eventSha256','compositionSha256'] as const) hex(receipt[name],name);
  w.setUint32(0,f.boxBytes); tail.set([117,117,105,100],4);
  tail.set(f.uuidHex.match(/../g)!.map(x=>parseInt(x,16)),8);
  tail[offset('version')] = 4; tail[offset('purpose')] = f.purposes[receipt.purpose];
  w.setUint32(offset('sampleOrdinal'),e.sampleIndex); w.setBigUint64(offset('decodeTime'),BigInt(e.decodeTime));
  w.setUint32(offset('timescale'),e.timescale);
  w.setBigUint64(offset('originalExpiresAtMs'),BigInt(receipt.originalExpiresAtMs));
  w.setBigUint64(offset('receiptSequence'),BigInt(receipt.receiptSequence));
  const digest = async (bytes:Uint8Array) => { const result=new Uint8Array(await crypto.subtle.digest('SHA-256',bytes.slice().buffer));current();return result; };
  const toHex = (bytes:Uint8Array) => Array.from(bytes,b=>b.toString(16).padStart(2,'0')).join('');
  const sampleHash = await digest(sample.bytes);
  const originalHash = await e.encodedSampleSha256; current();
  if(toHex(sampleHash) !== originalHash) fail();
  if(toHex(await digest(new TextEncoder().encode(serialized))) !== receipt.compositionSha256) fail();
  for(const [name,value] of [['collectorProcessIdSha256',receipt.collectorProcessId],['collectorGenerationSha256',receipt.collectorGeneration],['sourceIdSha256',e.frame.sourceId]]) {
    if(!value || [...value].length > 128) fail();
    tail.set(await digest(new TextEncoder().encode(value)),offset(name));
  }
  current(); tail.set(sampleHash,offset('sampleSha256'));
  const out = new Uint8Array(plain.length + tail.length);out.set(plain);out.set(tail,plain.length);return out;
}
